import { createHydratedEntityState } from 'libs/ngrx/legacy/lib/entity-collection/data';

import type { ID, PickActions } from '@core/ngrx-legacy';
import {
  createEntityCollectionStore,
  getEntityCollectionActions,
  pipeState,
  scopeFactory,
} from '@core/ngrx-legacy';
import {
  WorkflowAutomation,
  WorkflowAutomationCreatePayload,
} from '@recruitee/workflow-automations-types';

export const stageIdScope = (stageId: number) => scopeFactory('stageId', stageId);

const idSelector = ({ id }: { id: number }) => id;
const { actions, namespace, reducer, selectors } =
  createEntityCollectionStore<WorkflowAutomation>()('PipelineTemplateStageAutomations', {
    customActions: adapter => {
      const defaultActions = getEntityCollectionActions(adapter, idSelector);

      return {
        fetchStages: (state, payload: { pipelineTemplateId: number }) =>
          adapter.markAsPending(state),
        fetchStagesSuccess: (state, { data }) =>
          pipeState(
            state,
            next =>
              createHydratedEntityState(
                next,
                (<Array<{ id: number; workflowAutomations: WorkflowAutomation[] }>>data).reduce(
                  (acc, stageAutomation) => {
                    const scopeId = stageIdScope(stageAutomation.id);
                    acc[scopeId] = stageAutomation.workflowAutomations;
                    return acc;
                  },
                  <Record<string, WorkflowAutomation[]>>{},
                ),
                adapter,
              ),
            next => adapter.markAsLoaded(next, { scope: null, entities: [] }),
          ),
        fetchStagesFailed: defaultActions.fetchCollectionFailed,
        fetchOneStage: (
          state,
          payload: { pipelineTemplateId: number; stageId: number; scope: string },
        ) => adapter.markAsPending(state, { scope: null, entities: [payload.stageId] }),
        fetchOneStageSuccess: (state, { data, scope }) =>
          pipeState(
            state,
            next => adapter.setMany(next, data, { scope }),
            next =>
              adapter.markAsLoaded(next, { scope, entities: data.map(({ id }: { id: ID }) => id) }),
          ),
        fetchOneStageFailed: defaultActions.fetchOneFailed,
        createOnePessimistic: (
          state,
          payload: { data: WorkflowAutomationCreatePayload; scope: string; correlationId: string },
        ) => defaultActions.createOnePessimistic(state, payload),
        updateOnePessimistic: (
          state,
          payload: { data: WorkflowAutomation; scope: string; correlationId: string },
        ) => defaultActions.updateOnePessimistic(state, payload),
      };
    },
  });

const PipelineTemplateStageAutomationsActions: PickActions<
  typeof actions,
  | 'fetchStages'
  | 'fetchOneStage'
  | 'createOnePessimistic'
  | 'updateOnePessimistic'
  | 'removeOnePessimistic'
> = actions;

export {
  PipelineTemplateStageAutomationsActions,
  namespace as PipelineTemplateStageAutomationsNamespace,
  reducer as PipelineTemplateStageAutomationsReducer,
  selectors as PipelineTemplateStageAutomationsSelectors,
};
